import { type AIAgentType } from './AIAgent'

export type AccountOpeningPanelKind = 'typeSummary' | 'schemaForm' | 'holders'

export const AccountOpeningCollections = {
  ACCOUNTS: 'accounts',
  PARTIES: 'parties',
} as const

export type AccountOpeningCollection = (typeof AccountOpeningCollections)[keyof typeof AccountOpeningCollections]

export type AccountOpeningStepKind = 'collection' | 'operation' | 'handoff'

export type AccountOpeningItemLayout = 'card' | 'row' | 'plain'

export type AccountOpeningSchemaKey = 'accountTypeId' | 'role'

export type AccountOpeningConfirmScope = 'block' | 'item'

export type AccountOpeningFlowMode = 'edit' | 'readOnly'

export type AccountOpeningGate = string

export type AccountOpeningStepId = string

export interface AccountOpeningPrefill {
  agent: AIAgentType
  context: string[]
  requiresAnyOf?: string[][]
  clientRoles?: string[]
  label?: string
}

interface AccountOpeningTypeSummaryPanel {
  kind: 'typeSummary'
  editLabel?: string
}

export interface AccountOpeningHoldersCopy {
  heading?: string
  nameLabel?: string
  roleLabel?: string
  relationshipLabel?: string
  relationshipUnset?: string
  entityNamePlaceholder?: string
  personNamePlaceholder?: string
  addLabel?: string
  removeLabel?: string
  awaitingType?: string
  clientStatusLabel?: string
  clientStatusNew?: string
  clientStatusExisting?: string
  existingClientHint?: string
}

export interface AccountOpeningHoldersPanel {
  kind: 'holders'
  copy?: AccountOpeningHoldersCopy
}

export interface AccountOpeningSchemaFormPanel {
  kind: 'schemaForm'
  entity: string
  keyedBy: string
  valueFrom: AccountOpeningSchemaKey
  include?: string[]
  exclude?: string[]
  emptyHint?: string
  heading?: string
  sourceNote?: string
}

export type AccountOpeningPanel = AccountOpeningTypeSummaryPanel | AccountOpeningSchemaFormPanel | AccountOpeningHoldersPanel

export interface AccountOpeningExistingPartyView {
  include?: string[]
  exclude?: string[]
  heading?: string
}

interface AccountOpeningConfirm {
  scope: AccountOpeningConfirmScope
  label?: string
  readyMessage?: string
  blockedMessage?: string
  itemBlockedMessage?: string
  discardLabel?: string
}

interface AccountOpeningHeaderAction {
  kind: 'editIntake'
  label: string
}

export interface AccountOpeningCollectionCopy {
  gatedHint?: string
  loading?: string
  empty?: string
  railHeading?: string
  railMissing?: string
  railInvalid?: string
  railConfirm?: string
}

export interface AccountOpeningOperationCopy {
  body?: string
  action?: string
  actionAgain?: string
  pending?: string
  success?: string
  hint?: string
  warning?: string
  issuesHeading?: string
  lastRun?: string
  linkLabel?: string
  linkMissing?: string
  referenceLabel?: string
  notReady?: string
}

export interface AccountOpeningHandoffCopy {
  linkLabel?: string
  missing?: string
}

interface AccountOpeningStepBase {
  id: AccountOpeningStepId
  title: string
  subtitle: string
  readOnlyTitle?: string
  icon?: string
  modes: AccountOpeningFlowMode[]
  gate?: AccountOpeningGate
  requires?: AccountOpeningGate[]
}

export interface AccountOpeningCollectionStep extends AccountOpeningStepBase {
  kind: 'collection'
  collection: AccountOpeningCollection
  itemLayout: AccountOpeningItemLayout
  shared?: boolean
  panels: AccountOpeningPanel[]
  confirm?: AccountOpeningConfirm
  prefill?: AccountOpeningPrefill
  existingParty?: AccountOpeningExistingPartyView
  headerAction?: AccountOpeningHeaderAction
  copy?: AccountOpeningCollectionCopy
}

export interface AccountOpeningOperationStep extends AccountOpeningStepBase {
  kind: 'operation'
  operation: string
  irreversible?: boolean
  opensResultLink?: boolean
  copy?: AccountOpeningOperationCopy
}

export interface AccountOpeningHandoffStep extends AccountOpeningStepBase {
  kind: 'handoff'
  copy?: AccountOpeningHandoffCopy
}

export type AccountOpeningStep = AccountOpeningCollectionStep | AccountOpeningOperationStep | AccountOpeningHandoffStep

interface AccountOpeningEntities {
  accountType: string
  accountTypeSelector: string
  partyDetails?: string
}

export interface AccountOpeningItemCopy {
  accountHeading?: string
  submittedAccountHeading?: string
  accountSettingsHeading?: string
  partyNameLabel?: string
  partyRoleNote?: string
  partyFallbackName?: string
  existingPartySource?: string
  existingPartyNoReview?: string
}

export interface AccountOpeningOpeningsCopy {
  empty?: string
  draftStatus?: string
  submittedStatus?: string
  deleteTitle?: string
  deleteBody?: string
  deleteReference?: string
}

export interface AccountOpeningFlowCopy {
  editHeading?: string
  readOnlyHeading?: string
  intakeTitle?: string
  leaveLabel?: string
  doneMessage?: string
  doneLabel?: string
  custodianLabel?: string
  noAccountTypes?: string
  items?: AccountOpeningItemCopy
  openings?: AccountOpeningOpeningsCopy
}

export interface AccountOpeningTracking {
  terminalStatuses?: string[]
  refreshLabel?: string
  checkingLabel?: string
  checkedAtLabel?: string
  checkFailedLabel?: string
}

export interface AccountOpeningDescriptor {
  connector: string
  version: number
  entities: AccountOpeningEntities
  labels: {
    statusLabels: Record<string, string>
    statusTones?: Record<string, string>
  }
  tracking?: AccountOpeningTracking
  copy?: AccountOpeningFlowCopy
  steps: AccountOpeningStep[]
}
